import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_shree-3d-events/artifacts/c3s56qep_Shree-events-11-Final-Horizontal-600.png";

const links = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
      data-testid="site-navbar"
    >
      <div
        className={`mx-auto max-w-7xl flex items-center md:justify-between justify-center relative transition-all duration-500 ${
          scrolled
            ? "glass rounded-2xl py-3 px-4 md:px-6 mx-4 md:mx-auto"
            : "px-4 md:px-8"
        }`}
      >
        <a
          href="#top"
          className="flex items-center gap-3 md:static absolute left-1/2 md:left-auto md:translate-x-0 -translate-x-1/2"
          data-testid="brand-logo"
        >
          <span
            className={`relative inline-flex items-center justify-center transition-all duration-500 rounded-2xl ${
              scrolled
                ? "bg-gradient-to-br from-gold/[0.15] to-gold/[0.04] border border-gold/20 px-2 py-1 shadow-[0_2px_12px_rgba(212,175,55,0.18)]"
                : "bg-gradient-to-br from-gold/[0.10] to-transparent border border-gold/15 px-3 py-1.5 shadow-[0_4px_24px_rgba(212,175,55,0.20)]"
            }`}
          >
            <img
              src={LOGO_URL}
              alt="Shree Event Planner"
              className={`w-auto object-contain drop-shadow-[0_2px_14px_rgba(243,229,171,0.55)] transition-all duration-500 ${
                scrolled ? "h-9" : "h-14 md:h-16"
              }`}
              style={{
                filter:
                  "brightness(1.15) contrast(1.08) drop-shadow(0 0 6px rgba(243,229,171,0.35))",
              }}
            />
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm tracking-wider text-ivory/85 hover:text-gold transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-gold transition-all duration-500 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <a
          href="#contact"
          className="hidden md:inline-flex btn-gold text-sm"
          data-testid="nav-cta-button"
        >
          Plan Your Event
        </a>

        <button
          className="md:hidden text-ivory p-2 ml-auto relative z-10"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden mx-4 mt-3 glass rounded-2xl p-6 flex flex-col gap-4"
            data-testid="mobile-menu"
          >
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-ivory/90 hover:text-gold transition-colors py-1 tracking-wide"
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="btn-gold text-sm text-center mt-2"
              data-testid="mobile-cta-button"
            >
              Plan Your Event
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
